/**
 * Datadog browser logging. Buffers client-side logs and flushes them in
 * batches directly to Datadog with a public client token.
 *
 * Batching cuts request count ~10x — typical page load goes from 5–10 POSTs
 * to a single batched POST. Flushes on:
 *   • buffer reaches BATCH_MAX entries
 *   • BATCH_TIMEOUT_MS elapses since first buffered entry
 *   • the page is hidden (pagehide / visibilitychange) — uses sendBeacon so
 *     in-flight logs aren't dropped on navigation.
 */

import { createDirectDatadogTransport } from "./datadog-direct"

export type DatadogBrowserLogLevel = "info" | "warn" | "error"
const transport = createDirectDatadogTransport({ webappUrl: "", runtime: "browser" })
let unloadHooksAttached = false

// Every frame of our own bundle is served from /_next/static, so a window.onerror
// stack with no such frame was thrown by injected third-party code (extensions,
// SEO scanners) that carries our page origin as its filename — the extension-URL
// check in GlobalErrorListeners cannot see those. Requires a non-empty stack:
// a stackless event is ambiguous and must still ship. Scoped to window.onerror
// so unhandled_rejection and explicit logger calls are never touched.
const APP_STACK_FRAME = "_next/static"

function isForeignWindowError(attributes?: Record<string, unknown>): boolean {
  if (!attributes || attributes.capture !== "window.onerror") return false
  const stack = attributes.error_stack
  if (typeof stack !== "string" || stack.trim() === "") return false
  return !stack.includes(APP_STACK_FRAME)
}

function attachUnloadHooks() {
  if (unloadHooksAttached) return
  unloadHooksAttached = true

  // pagehide fires reliably on real navigations and tab close (unlike
  // beforeunload which is unreliable on mobile).
  window.addEventListener("phantom-auth-changed", () => transport.invalidateContext())
  window.addEventListener("pagehide", () => transport.flushOnUnload())
  // visibilitychange catches tab switches and PWA backgrounding.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") transport.flushOnUnload()
  })
}

export function sendToDatadogBrowser(
  level: DatadogBrowserLogLevel,
  message: string,
  attributes?: Record<string, unknown>
): void {
  if (typeof window === "undefined") return
  if (!message) return
  if (isForeignWindowError(attributes)) return

  attachUnloadHooks()

  transport.send(level, message, attributes)
}

/** Flush an event produced by another pagehide listener after it was queued. */
export function flushDatadogBrowserOnUnload(): void {
  if (typeof window === "undefined") return
  transport.flushOnUnload()
}
